import {
  Item,
  ItemRestriction,
  OPERATORS,
  PROMOTION_TYPE,
  SellerOperator,
  SellerRestriction,
} from 'typings'
import { Promotion, RegularScopeApplyTo } from '../../../typings'

export type GenericScope = Partial<
  Record<GENERIC_SCOPE_OPTIONS, ItemRestriction>
>

export enum GENERIC_SCOPE_OPTIONS {
  SKUS = 'skus',
  PRODUCTS = 'products',
  COLLECTIONS = 'collections',
  CATEGORIES = 'categories',
  BRANDS = 'brands',
  SELLERS = 'sellers',
  SALES_CHANNELS = 'salesChannels',
}

/**
 * Normalizes a promotion trigger into a generic scope, merged with the previous restrictions scope.
 *
 * Behavior:
 * - When SKUs has items, adds a scope that targets those SKUs.
 * - When collections has items, adds a scope that targets those collections
 *   while explicitly excluding the SKUs to prevent double counting.
 *
 * @param {GenericScope | undefined} restrictionsScope Generic scope built from restrictions.
 * @param {{ skus: Item[]; collections: Item[] } | undefined} trigger Trigger containing SKUs and Collections.
 * @returns {Array<GenericScope> | undefined} Trigger generic scope.
 */
export function buildScopeFromTrigger(
  restrictionsScope: GenericScope | undefined,
  trigger:
    | {
        skus: Item[]
        collections: Item[]
      }
    | undefined
): Array<GenericScope> | undefined {
  if (!trigger) return undefined

  const { skus, collections } = trigger
  const scope: Array<GenericScope> = []

  if (skus.length) {
    scope.push({
      ...restrictionsScope,
      [GENERIC_SCOPE_OPTIONS.SKUS]: {
        items: skus,
        operator: OPERATORS.EQUALS,
      },
    })
  }

  if (collections.length) {
    scope.push({
      ...restrictionsScope,
      [GENERIC_SCOPE_OPTIONS.COLLECTIONS]: {
        items: collections,
        operator: OPERATORS.EQUALS,
      },
      [GENERIC_SCOPE_OPTIONS.SKUS]: {
        items: skus,
        operator: OPERATORS.DIFFERENT,
      },
    })
  }

  return scope
}

/**
 * Normalizes a sellers restriction into a generic scope.
 * The scope is returned for equals or different operator.
 * No scope is returned when no sellers is provider or the operator is all or opt-in.
 *
 * @param {SellerRestriction | undefined} sellers Sellers restriction.
 * @returns {GenericScope | undefined}
 */
export function buildSellersScope(
  sellers: SellerRestriction | undefined
): GenericScope | undefined {
  if (!sellers) return undefined

  switch (sellers.operator) {
    case SellerOperator.DIFFERENT:
      return {
        [GENERIC_SCOPE_OPTIONS.SELLERS]: {
          items: sellers.items,
          operator: OPERATORS.DIFFERENT,
        },
      }
    case SellerOperator.EQUALS:
      return {
        [GENERIC_SCOPE_OPTIONS.SELLERS]: {
          items: sellers.items,
          operator: OPERATORS.EQUALS,
        },
      }
    default:
      return undefined
  }
}

/**
 * Normalizes a sales channels restriction into a GenericScope. If none is provided,
 * returns a scope that effectively targets all sales channels.
 *
 * @param {ItemRestriction | undefined} salesChannels Sales channels restriction.
 * @returns {GenericScope} Sales channels scope.
 */
export function buildSalesChannelScope(
  salesChannels: ItemRestriction | undefined
): GenericScope | undefined {
  if (!salesChannels) return undefined

  const { items, operator } = salesChannels

  return {
    [GENERIC_SCOPE_OPTIONS.SALES_CHANNELS]: {
      items,
      operator,
    },
  }
}

/**
 * Combines all restrictions into a single generic scope.
 * Returns undefined when neither restriction yields a scope.
 *
 * Supported restrictions are:
 * - Sellers
 * - Sales Channel
 *
 * @param {Promotion['restrictions']} restrictions
 * @returns {GenericScope | undefined}
 */
export function buildRestrictionsScope(
  restrictions: Promotion['restrictions']
): GenericScope | undefined {
  const { sellers, salesChannels } = restrictions

  const sellersScope = buildSellersScope(sellers)
  const salesChannelsScope = buildSalesChannelScope(salesChannels)

  if (!sellersScope && !salesChannelsScope) return undefined

  return {
    ...sellersScope,
    ...salesChannelsScope,
  }
}

/**
 * Builds a normalized, generic item scope for a given promotion.
 * It converts the promotion's type-specific scope definitions into a common structure consumable by count APIs.
 *
 * The scope takes the following criterias into account:
 *  - Products
 *  - SKUs
 *  - Collections
 *  - Brands
 *  - Categories
 *  - Sellers
 *  - Sales Channels
 *
 * @param {Promotion} promotion current promotion
 *
 * @returns {Array<GenericScope> | undefined} An array of GenericScope objects when a defined scope exists or
 * undefined when the scope targets all items or no items.
 */
export function buildGenericScope(
  promotion: Promotion
): Array<GenericScope> | undefined {
  const restrictionsScope: GenericScope | undefined = buildRestrictionsScope(
    promotion.restrictions
  )

  switch (promotion.type) {
    case PROMOTION_TYPE.REGULAR:
      const { regular } = promotion

      if (
        !regular.scope ||
        regular.scope.applyTo === RegularScopeApplyTo.allItems
      )
        return restrictionsScope ? [restrictionsScope] : undefined

      return [{ ...restrictionsScope, ...regular.scope }]
    case PROMOTION_TYPE.CAMPAIGN:
      const { campaign } = promotion

      if (
        !campaign.scope ||
        campaign.scope.applyTo === RegularScopeApplyTo.allItems
      )
        return restrictionsScope ? [restrictionsScope] : undefined

      return [{ ...restrictionsScope, ...campaign.scope }]
    case PROMOTION_TYPE.BUY_TOGETHER: {
      const { benefit, trigger } = promotion.buyTogether

      if (!benefit || !trigger) return undefined

      const skus: Item[] = benefit.skus.concat(trigger.skus)

      return [
        {
          ...restrictionsScope,
          [GENERIC_SCOPE_OPTIONS.SKUS]: {
            items: skus,
            operator: OPERATORS.EQUALS,
          },
        },
      ]
    }
    case PROMOTION_TYPE.BUY_AND_WIN:
      return buildScopeFromTrigger(
        restrictionsScope,
        promotion.buyAndWin.trigger
      )

    case PROMOTION_TYPE.MORE_FOR_LESS:
      return buildScopeFromTrigger(
        restrictionsScope,
        promotion.moreForLess.trigger
      )

    case PROMOTION_TYPE.PROGRESSIVE:
      return buildScopeFromTrigger(
        restrictionsScope,
        promotion.progressive.trigger
      )
  }
}
